'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Download, Image, FileText, Users, Calendar, Award, ExternalLink } from 'lucide-react'
import Header from '../components/Header'
import Footer from '../components/Footer'
import { pressKitApi } from '@/lib/api'
import type { PressKitItem } from '@/lib/types'

const companyFacts = [
  { label: 'Founded', value: 'March 2025' },
  { label: 'Headquarters', value: 'London, United Kingdom' },
  { label: 'Industry', value: 'EdTech & Privacy Technology' },
  { label: 'Stage', value: 'Pre-Launch Development' },
  { label: 'Products', value: '2 in Development' },
  { label: 'Focus Areas', value: 'Youth Entrepreneurship, Age Verification' }
]

const keyMessages = [
  {
    title: 'Education for Families',
    message: 'Elyndra Studios is building Sparky Ventures, a platform for families, in development, where parents help children learn business basics.'
  },
  {
    title: 'Privacy-First Technology',
    message: 'Rebel Verify: privacy-first age verification in development, designed so sites don\'t need to see your date of birth.'
  },
  {
    title: 'Early-Stage Innovation',
    message: 'As a pre-launch company, we\'re building the foundation for next-generation digital education and privacy protection technologies.'
  }
]

export default function PressKit() {
  const [pressKitItems, setPressKitItems] = useState<PressKitItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPressKitItems = async () => {
      try {
        const { data, error } = await pressKitApi.getAll()
        
        if (error) {
          console.warn('Press kit API error:', error)
          setPressKitItems([])
        } else {
          setPressKitItems(data || [])
        }
      } catch (err) {
        console.warn('Press kit fetch error:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch press kit items')
        setPressKitItems([])
      } finally {
        setLoading(false)
      }
    }

    fetchPressKitItems()
  }, [])

  // Group press kit items by category
  const groupedItems = pressKitItems.reduce((acc, item) => {
    const category = item.category || 'Other'
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(item)
    return acc
  }, {} as Record<string, PressKitItem[]>)

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size'
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  const getIconForCategory = (category: string) => {
    const lowerCategory = category.toLowerCase()
    if (lowerCategory.includes('logo') || lowerCategory.includes('image') || lowerCategory.includes('screenshot')) {
      return Image
    }
    return FileText
  }
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Press <span className="gradient-text">Kit</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Media resources, company information, and assets for journalists, 
                partners, and industry analysts covering Elyndra Studios.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Company <span className="gradient-text">Overview</span>
              </h2>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl font-bold mb-6">About Elyndra Studios Ltd</h3>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Elyndra Studios Ltd is an early-stage technology company developing innovative 
                  solutions that bridge the gap between education and digital innovation. 
                  We are building platforms that will empower young minds.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Our flagship products include Sparky Ventures, a platform for families, in development, where 
                  parents help children learn business basics, and Rebel Verify: privacy-first age 
                  verification in development, designed so sites don&apos;t need to see your date of birth.
                </p>
                <motion.a
                  href="/#contact"
                  className="btn-primary inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Media Inquiries
                  <ExternalLink className="w-4 h-4 ml-2" />
                </motion.a>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-lg"
              >
                <h4 className="text-xl font-bold mb-6">Company Facts</h4>
                <div className="space-y-4">
                  {companyFacts.map((fact, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                      <span className="font-medium text-gray-700">{fact.label}</span>
                      <span className="text-gray-600">{fact.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Key Messages */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Key <span className="gradient-text">Messages</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Core messaging points for media coverage and industry discussions.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
              {keyMessages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card text-center"
                >
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{message.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{message.message}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Media Assets */}
        <section className="py-16 px-4 bg-gradient-to-br from-primary-50 to-secondary-50">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Media <span className="gradient-text">Assets</span>
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                High-resolution logos, screenshots, and documents for media use.
              </p>
            </motion.div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-600 mb-4">Error loading press kit items: {error}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  className="btn-primary"
                >
                  Try Again
                </button>
              </div>
            ) : Object.keys(groupedItems).length > 0 ? (
              <div className="space-y-12">
                {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
                  <motion.div
                    key={category}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <h3 className="text-2xl font-bold mb-6">{category}</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {items.map((item, itemIndex) => {
                        const IconComponent = getIconForCategory(category)
                        return (
                          <div key={item.id} className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-800 mb-2">{item.title}</h4>
                                {item.description && (
                                  <p className="text-sm text-gray-600 mb-2">{item.description}</p>
                                )}
                                <div className="flex items-center gap-4 text-sm text-gray-600">
                                  <span>{item.file_type || 'Unknown'}</span>
                                  <span>{formatFileSize(item.file_size)}</span>
                                </div>
                              </div>
                              <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center flex-shrink-0">
                                <IconComponent className="w-5 h-5 text-white" />
                              </div>
                            </div>
                            <motion.a
                              href={item.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full flex items-center justify-center gap-2 py-2 px-4 border-2 border-primary-200 text-primary-700 rounded-xl hover:bg-primary-50 transition-colors"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </motion.a>
                          </div>
                        )
                      })}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">No Media Assets Available</h3>
                <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
                  We&apos;re currently preparing our press kit materials. Please check back soon or contact us directly for specific media requests.
                </p>
                <motion.a
                  href="/#contact"
                  className="btn-primary inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact for Media Assets
                  <ExternalLink className="w-4 h-4 ml-2" />
                </motion.a>
              </div>
            )}

            {/* Download All */}
            {Object.keys(groupedItems).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center mt-16"
              >
                <motion.button
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download Complete Press Kit
                </motion.button>
              </motion.div>
            )}
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-4">Media Contact</h3>
                <p className="text-lg mb-6 opacity-90">
                  For press inquiries, interviews, or additional information about Elyndra Studios.
                </p>
                <div className="space-y-2 mb-6">
                  <p className="text-lg">Email: press@elyndrastudios.com</p>
                  <p className="text-lg">General: hello@elyndrastudios.com</p>
                </div>
                <motion.a
                  href="/#contact"
                  className="bg-white text-primary-600 font-semibold py-3 px-8 rounded-2xl hover:bg-gray-50 transition-colors inline-flex items-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Us
                  <ExternalLink className="w-4 h-4 ml-2" />
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

    </div>
  )
}
